import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Trash, LinkSimple, WifiHigh, WarningCircle, Camera, Broadcast,
} from "@phosphor-icons/react";

const ZONES = ["Retail", "Checkout", "Workplace", "Restricted", "Perimeter"];
const MONITORS = ["Theft", "Vandalism", "Productivity", "Interaction", "Wait"];

export default function CameraConnect() {
  const [cams, setCams] = useState([]);
  const [form, setForm] = useState({ name: "", zone: "Retail", rtsp_url: "", snapshot_url: "", monitors: ["Theft"] });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/cameras").then((r) => setCams(r.data));
  useEffect(() => { load(); }, []);

  const toggleMonitor = (m) =>
    setForm((f) => ({ ...f, monitors: f.monitors.includes(m) ? f.monitors.filter((x) => x !== m) : [...f.monitors, m] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Give the camera a name"); return; }
    if (!form.rtsp_url.trim() && !form.snapshot_url.trim()) { toast.error("Add an RTSP URL or a snapshot URL"); return; }
    setBusy(true);
    try {
      await api.post("/cameras", form);
      toast.success("Camera connected");
      setForm({ name: "", zone: "Retail", rtsp_url: "", snapshot_url: "", monitors: ["Theft"] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add camera");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/cameras/${id}`);
      toast.success("Camera removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  const custom = cams.filter((c) => c.custom);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-5">
        <form onSubmit={submit} className="bg-[#121214] border border-white/10 p-4" data-testid="camera-connect-form">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
            <Plus size={16} weight="bold" className="text-[#007AFF]" /> Connect a Camera
          </h2>
          <p className="text-xs text-[#A1A1AA] mb-4 leading-relaxed">
            Add any RTSP/ONVIF camera. Provide an <span className="text-[#3395FF]">HTTP snapshot URL</span> and SentryVision pulls & analyzes frames directly in Live Watch. RTSP-only cameras are registered as <span className="text-[#FF9F0A]">pending</span> until you run the on-prem edge agent.
          </p>

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Camera Name</label>
          <input data-testid="camera-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Liquor Aisle West" className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#007AFF] transition-colors mb-4" />

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Zone</label>
          <select data-testid="camera-zone-select" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}
            className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#007AFF] transition-colors mb-4">
            {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">RTSP / ONVIF URL</label>
          <input data-testid="camera-rtsp-input" value={form.rtsp_url} onChange={(e) => setForm({ ...form, rtsp_url: e.target.value })}
            placeholder="rtsp://user:pass@192.168.1.42:554/stream1" className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm font-mono-data outline-none focus:border-[#007AFF] transition-colors mb-4" />

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Snapshot URL <span className="text-[#71717A] normal-case tracking-normal">(recommended — enables live cloud analysis)</span></label>
          <input data-testid="camera-snapshot-input" value={form.snapshot_url} onChange={(e) => setForm({ ...form, snapshot_url: e.target.value })}
            placeholder="http://192.168.1.42/onvif/snapshot" className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm font-mono-data outline-none focus:border-[#007AFF] transition-colors mb-4" />

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">AI Monitors</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {MONITORS.map((m) => (
              <button type="button" key={m} data-testid={`monitor-${m}`} onClick={() => toggleMonitor(m)}
                className={`px-2.5 py-1 text-xs font-mono-data border transition-colors ${form.monitors.includes(m) ? "bg-[#007AFF]/15 text-[#3395FF] border-[#007AFF]/50" : "bg-[#1A1A1E] text-[#71717A] border-white/10 hover:text-white"}`}>
                {m}
              </button>
            ))}
          </div>

          <button data-testid="camera-connect-submit" type="submit" disabled={busy}
            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors disabled:opacity-60">
            {busy ? "Connecting…" : "Connect Camera"}
          </button>
        </form>
      </div>

      <div className="xl:col-span-7">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-3">Connected Cameras</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cams.map((c) => (
            <div key={c.id} data-testid={`connected-cam-${c.id}`} className="bg-[#121214] border border-white/10 p-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Camera size={18} weight="bold" className="text-[#71717A]" />
                  <div>
                    <p className="text-sm font-medium leading-none">{c.name}</p>
                    <p className="font-mono-data text-[10px] text-[#71717A] mt-1">{c.zone} · {c.ai}</p>
                  </div>
                </div>
                {c.custom && (
                  <button data-testid={`delete-cam-${c.id}`} onClick={() => remove(c.id)} className="text-[#71717A] hover:text-[#FF3B30] transition-colors">
                    <Trash size={16} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                {c.status === "online" && <span className="flex items-center gap-1 font-mono-data text-[10px] px-1.5 py-0.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40"><WifiHigh size={11} weight="bold" /> LIVE</span>}
                {c.status === "pending" && <span className="flex items-center gap-1 font-mono-data text-[10px] px-1.5 py-0.5 bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40"><WarningCircle size={11} weight="bold" /> PENDING EDGE AGENT</span>}
                {c.status === "offline" && <span className="font-mono-data text-[10px] px-1.5 py-0.5 bg-[#71717A]/15 text-[#A1A1AA] border border-[#71717A]/40">OFFLINE</span>}
                {c.custom ? <span className="font-mono-data text-[10px] text-[#71717A]">CUSTOM</span> : <span className="font-mono-data text-[10px] text-[#71717A]">DEMO</span>}
              </div>
              {(c.rtsp_url || c.snapshot_url) && (
                <p className="font-mono-data text-[10px] text-[#71717A] mt-2 truncate flex items-center gap-1">
                  <LinkSimple size={11} /> {c.snapshot_url || c.rtsp_url}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4 mt-4 flex items-start gap-3">
          <Broadcast size={20} weight="fill" className="text-[#3395FF] shrink-0 mt-0.5" />
          <p className="text-sm text-[#E4E4E7] leading-relaxed">
            Cameras marked <span className="text-[#32D74B] font-medium">LIVE</span> are picked up automatically by <span className="font-medium">Live Watch</span> for continuous AI analysis. For RTSP-only cameras on a private network, install the SentryVision edge agent on-site (see System Architecture) — it relays snapshots securely so the cloud never touches your LAN directly.
          </p>
        </div>
      </div>
    </div>
  );
}
